import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { shipmentReturnService } from "../../services/shipmentReturnService"
import { useAuth } from "../../context/AuthContext"
import Alert from "../common/Alert"
import { ArrowLeft, Pencil, RotateCcw } from "lucide-react";
import { PageLayout, Button, StatusBadge, DetailCard } from "../common/index.js";

const CARD_CLS = "bg-white rounded-card shadow-card p-3 sm:p-4 mb-4"
const INNER_CLS = "bg-gray-50 rounded-input p-3 sm:p-4"

const EditReturn = () => {
  const { returnId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [paymentInitiateLoading, setPaymentInitiateLoading] = useState(false)
  const [paymentRefundLoading, setPaymentRefundLoading] = useState(false)
  const [returnData, setReturnData] = useState(null)

  const [paymentRefundData, setPaymentRefundData] = useState({
    approved: false,
    payment_receipt: null,
    payment_receipt_file: null,
    imagePreview: null,
  })

  useEffect(() => {
    fetchReturn()
  }, [returnId])

  const fetchReturn = async () => {
    try {
      setLoading(true)
      const response = await shipmentReturnService.getReturn(returnId)
      const returnInfo = response.data.return || response.data.data

      if (returnInfo) {
        setReturnData(returnInfo)
        setPaymentRefundData({
          approved: false,
          payment_receipt: null,
          payment_receipt_file: null,
          imagePreview: null,
        })
      } else {
        Alert.error("Return request not found")
      }
    } catch (error) {
      console.error("Error fetching return:", error)
      Alert.error(error.response?.data?.error || "Failed to load return request")
    } finally {
      setLoading(false)
    }
  }

  const canApprove = ["System Admin", "Admin", "Accountant"].includes(user?.role)

  const handleInitiatePayment = async () => {
    const confirmed = await Alert.confirm(
      "Are you sure you want to initiate the payment for this return?",
      "Initiate Payment",
    )

    if (!confirmed) return

    try {
      setPaymentInitiateLoading(true)
      await shipmentReturnService.initiatePayment(returnId)
      Alert.success("Payment initiated successfully!")
      await fetchReturn()
    } catch (error) {
      console.error("Error initiating payment:", error)
      Alert.error(error.response?.data?.error || "Failed to initiate payment")
    } finally {
      setPaymentInitiateLoading(false)
    }
  }

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "open":
        return "📖"
      case "close":
        return "✅"
      case "pending":
        return "⏳"
      case "approved":
        return "✅"
      case "rejected":
        return "❌"
      default:
        return "📋"
    }
  }

  const handleMarkPaymentRefunded = async () => {
    if (!paymentRefundData.approved) {
      Alert.error("Please check the payment refund approval checkbox")
      return
    }

    const confirmed = await Alert.confirm(
      'Are you sure you want to mark the payment as refunded? The shipment status will be updated to "Returned".',
      "Confirm Payment Refund",
    )

    if (!confirmed) return

    try {
      setPaymentRefundLoading(true)
      const formData = new FormData()

      if (paymentRefundData.payment_receipt_file) {
        formData.append("payment_receipt", paymentRefundData.payment_receipt_file)
      }

      await shipmentReturnService.markPaymentRefunded(returnId, formData)
      Alert.success("Payment marked as refunded successfully!")
      navigate("/returns")
    } catch (error) {
      console.error("Error marking payment refunded:", error)
      Alert.error(error.response?.data?.error || "Failed to mark payment refunded")
    } finally {
      setPaymentRefundLoading(false)
    }
  }

  const handleReceiptFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 5242880) {
        Alert.error("File size must be less than 5MB")
        return
      }

      const imagePreview = null
      if (file.type.startsWith("image/")) {
        const reader = new FileReader()
        reader.onload = (event) => {
          setPaymentRefundData((prev) => ({
            ...prev,
            imagePreview: event.target.result,
          }))
        }
        reader.readAsDataURL(file)
      }

      setPaymentRefundData({
        ...paymentRefundData,
        payment_receipt_file: file,
        payment_receipt: file.name,
        imagePreview,
      })
    }
  }

  const getStatusBadgeColor = (status) => {
    const colors = {
      open: "bg-yellow-100 text-yellow-800 border-yellow-200",
      close: "bg-green-100 text-green-800 border-green-200",
    }
    return colors[status?.toLowerCase()] || "bg-gray-100 text-gray-800 border-gray-200"
  }

  if (loading) {
    return (
      <PageLayout title="Manage Return Request" icon={RotateCcw}>
        <div className="flex items-center justify-center py-16">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
            <p className="text-gray-600">Loading return request...</p>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (!returnData) {
    return (
      <PageLayout title="Manage Return Request" icon={RotateCcw}>
        <div className="flex items-center justify-center py-16">
          <div className="bg-white rounded-card shadow-card border border-gray-200 p-8 max-w-md text-center">
            <p className="text-red-600 text-lg font-bold mb-2">Return Request Not Found</p>
            <p className="text-gray-600 mb-4">The return request you are looking for does not exist.</p>
            <Button onClick={() => navigate("/returns")}>Back to Returns</Button>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (!canApprove) {
    return (
      <PageLayout title="Manage Return Request" icon={RotateCcw}>
        <div className="flex items-center justify-center py-16">
          <div className="bg-white rounded-card shadow-card border border-gray-200 p-8 max-w-md text-center">
            <p className="text-red-600 text-lg font-bold mb-2">Access Denied</p>
            <p className="text-gray-600 mb-2">You do not have permission to manage this return request.</p>
            <p className="text-sm text-gray-500 mb-4">
              Only Admin, System Admin, and Accountant roles can perform this action.
            </p>
            <Button onClick={() => navigate("/returns")}>Back to Returns</Button>
          </div>
        </div>
      </PageLayout>
    )
  }

  const isPaymentInitiated = returnData?.payment_initiated?.approved || false
  const isPaymentRefunded = returnData?.payment_refunded?.approved || false

  return (
    <PageLayout
      title="Manage Return Request"
      icon={RotateCcw}
      maxWidth="max-w-7xl"
      actions={
        <>
          <Button variant="outline" onClick={() => navigate(`/shipments/view/${returnData.shipment_id}`)}>
            View Shipment
          </Button>
          <Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/returns')}>
            Back
          </Button>
        </>
      }
    >
      <div className="mb-4 p-4 bg-white rounded-card shadow-card border border-gray-100">
        <div className="flex items-center gap-2">
          <StatusBadge status={returnData.return_status} />
          <span className="text-sm text-gray-600">
            {returnData.return_status?.toLowerCase() === 'open' && 'Processing payment workflow'}
            {returnData.return_status?.toLowerCase() === 'close' && 'Return request completed'}
          </span>
        </div>
      </div>

                {/* Return Details Card */}
                <div className={CARD_CLS}>
                  <h3 className="text-sm font-semibold text-secondary mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
                    Return Details
                  </h3>

                  <div>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                      <div className={INNER_CLS}>
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Return ID</p>
                        <p className="font-bold text-gray-900 font-mono">#{returnData.return_id}</p>
                      </div>
                      <div className={INNER_CLS}>
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Shipment ID</p>
                        <button
                          onClick={() => navigate(`/shipments/view/${returnData.shipment_id}`)}
                          className="font-bold text-blue-600 font-mono hover:underline"
                        >
                          #{returnData.shipment_id}
                        </button>
                      </div>
                      <div className={INNER_CLS}>
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Created By</p>
                        <p className="text-gray-900 font-semibold">{returnData.created_by || "N/A"}</p>
                      </div>
                      <div className={INNER_CLS}>
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Created Date</p>
                        <p className="text-gray-900">
                          {returnData.created_at
                            ? new Date(returnData.created_at).toLocaleString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Processing Ladder */}
                <div className={CARD_CLS}>
                  <h3 className="text-sm font-semibold text-secondary mb-3 pb-2 border-b border-gray-200">
                    Payment Processing Ladder
                  </h3>

                  <div>
                    {/* Step 1: Payment Initiated */}
                    <div className="mb-8">
                      <div className="flex items-start gap-4">
                        <div
                          className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-white text-lg ${
                            isPaymentInitiated ? "bg-green-500" : "bg-brand"
                          }`}
                        >
                          1
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-3">
                            <h4 className="text-base font-bold text-gray-900">Step 1: Initiate Payment</h4>
                            {isPaymentInitiated && (
                              <span className="text-green-600 text-xs font-bold px-2 py-1 bg-green-100 rounded">
                                ✓ COMPLETED
                              </span>
                            )}
                          </div>

                          {isPaymentInitiated ? (
                            <div className="bg-green-50 border border-green-200 p-4 rounded-input mb-4">
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Initiated By:</span>
                                  <span className="font-semibold text-gray-900">
                                    {returnData.payment_initiated?.initiated_by?.user_name || "N/A"}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Timestamp:</span>
                                  <span className="font-semibold text-gray-900">
                                    {returnData.payment_initiated?.timestamp
                                      ? new Date(returnData.payment_initiated.timestamp).toLocaleString("en-IN")
                                      : "N/A"}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <Button
                              onClick={handleInitiatePayment}
                              disabled={paymentInitiateLoading}
                              loading={paymentInitiateLoading}
                              size="sm"
                            >
                              Initiate Payment
                            </Button>
                          )}
                        </div>
                      </div>
                      {!isPaymentRefunded && <div className="ml-6 h-8 border-l-2 border-gray-300 mt-4"></div>}
                    </div>

                    {/* Step 2: Payment Refunded */}
                    <div>
                      <div className="flex items-start gap-4">
                        <div
                          className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-white text-lg ${
                            isPaymentRefunded ? "bg-green-500" : isPaymentInitiated ? "bg-blue-600" : "bg-gray-300"
                          }`}
                        >
                          2
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-3">
                            <h4 className="text-base font-bold text-gray-900">Step 2: Approve & Refund Payment</h4>
                            {isPaymentRefunded && (
                              <span className="text-green-600 text-xs font-bold px-2 py-1 bg-green-100 rounded">
                                ✓ COMPLETED
                              </span>
                            )}
                          </div>

                          {isPaymentInitiated ? (
                            isPaymentRefunded ? (
                              <div className="bg-green-50 border border-green-200 p-4 rounded-input">
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Refunded By:</span>
                                    <span className="font-semibold text-gray-900">
                                      {returnData.payment_refunded?.initiated_by?.user_name || "N/A"}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Timestamp:</span>
                                    <span className="font-semibold text-gray-900">
                                      {returnData.payment_refunded?.timestamp
                                        ? new Date(returnData.payment_refunded.timestamp).toLocaleString("en-IN")
                                        : "N/A"}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-4">
                                <div>
                                  <label className="flex items-center gap-3 p-3 border border-blue-200 rounded-input cursor-pointer hover:bg-blue-50 transition">
                                    <input
                                      type="checkbox"
                                      checked={paymentRefundData.approved}
                                      onChange={(e) =>
                                        setPaymentRefundData({
                                          ...paymentRefundData,
                                          approved: e.target.checked,
                                        })
                                      }
                                      disabled={paymentRefundLoading}
                                      className="w-5 h-5 text-blue-500 rounded cursor-pointer"
                                    />
                                    <div>
                                      <p className="font-semibold text-gray-900">I approve this refund</p>
                                      <p className="text-xs text-gray-500">Mark this payment as successfully refunded</p>
                                    </div>
                                  </label>
                                </div>

                                <div className="border-t border-blue-200 pt-4">
                                  <label className="block text-sm font-bold text-gray-900 mb-2">
                                    Payment Receipt (Optional)
                                  </label>
                                  <div className="relative">
                                    <input
                                      type="file"
                                      id="payment_receipt"
                                      onChange={handleReceiptFileChange}
                                      disabled={paymentRefundLoading}
                                      className="hidden"
                                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.gif,.webp"
                                    />
                                    <label
                                      htmlFor="payment_receipt"
                                      className="flex items-center justify-center px-4 py-3 border border-dashed border-blue-300 rounded-input cursor-pointer hover:border-brand transition-colors"
                                    >
                                      <div className="text-center">
                                        {paymentRefundData.payment_receipt ? (
                                          <>
                                            <svg
                                              className="w-6 h-6 text-green-600 mx-auto mb-1"
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path
                                                fillRule="evenodd"
                                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                                clipRule="evenodd"
                                              />
                                            </svg>
                                            <p className="text-sm font-semibold text-gray-900">
                                              {paymentRefundData.payment_receipt}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">Click to change file</p>
                                          </>
                                        ) : (
                                          <>
                                            <svg
                                              className="w-6 h-6 text-gray-400 mx-auto mb-1"
                                              fill="none"
                                              stroke="currentColor"
                                              viewBox="0 0 24 24"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 4v16m8-8H4"
                                              />
                                            </svg>
                                            <p className="text-sm font-semibold text-gray-700">Upload Payment Receipt</p>
                                            <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG or DOC (max 5MB)</p>
                                          </>
                                        )}
                                      </div>
                                    </label>
                                  </div>

                                  {paymentRefundData.imagePreview && (
                                    <div className="mt-4 p-3 bg-gray-50 rounded-input border border-gray-200">
                                      <p className="text-xs font-semibold text-gray-500 mb-2">Preview:</p>
                                      <img
                                        src={paymentRefundData.imagePreview || "/placeholder.svg"}
                                        alt="Receipt preview"
                                        className="max-w-xs h-auto rounded-input border border-gray-300 shadow-sm"
                                      />
                                    </div>
                                  )}
                                </div>

                                {/* Refund Button */}
                                <Button
                                  onClick={handleMarkPaymentRefunded}
                                  disabled={paymentRefundLoading || !paymentRefundData.approved}
                                  loading={paymentRefundLoading}
                                  variant="success"
                                  className="w-full"
                                >
                                  Mark Payment as Refunded
                                </Button>
                              </div>
                            )
                          ) : (
                            <div className="bg-gray-50 border border-gray-200 p-4 rounded-input text-center">
                              <p className="text-gray-600 text-sm">
                                <span className="text-lg">⏳</span> Waiting for payment initiation
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
    </PageLayout>
  )
}

export default EditReturn;